import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { DG } from '@cotera/sdk/core';
import { Icon, Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';

/**
 * A row as stored on the node. Kept raw (an unfinished or duplicate name is
 * persisted as typed) so the editor never eats what someone is typing;
 * `DG.parseInputShapeFields` drops unusable rows when the agent runs.
 */
export type InputShapeRow = {
  name: string;
  type: DG.InputShapeFieldType;
  description: string | null;
};

const TYPE_LABELS: Record<DG.InputShapeFieldType, string> = {
  string: 'String',
  number: 'Number',
  boolean: 'Boolean',
};

const isFieldType = (value: string): value is DG.InputShapeFieldType =>
  (DG.INPUT_SHAPE_FIELD_TYPES as readonly string[]).includes(value);

const readRows = (raw: unknown): InputShapeRow[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((entry): InputShapeRow[] => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const { name, type, description } = entry as Record<string, unknown>;
    return [
      {
        name: typeof name === 'string' ? name : '',
        type: typeof type === 'string' && isFieldType(type) ? type : 'string',
        description: typeof description === 'string' ? description : null,
      },
    ];
  });
};

const nameError = (
  row: InputShapeRow,
  index: number,
  rows: InputShapeRow[]
): string | null => {
  const name = row.name.trim();
  if (name === '') {
    return null;
  }
  if (!DG.INPUT_SHAPE_KEY_PATTERN.test(name)) {
    return 'Letters, numbers and _ only; cannot start with a number.';
  }
  if (rows.findIndex((r) => r.name.trim() === name) !== index) {
    return 'Duplicate name.';
  }
  return null;
};

export const InputShapeNodeView = ({
  node,
  updateAttributes,
  editor,
}: NodeViewProps) => {
  const rows = readRows(node.attrs['fields']);
  const editable = editor.isEditable;

  const setRows = (next: InputShapeRow[]) => {
    updateAttributes({ fields: next });
  };

  const updateRow = (index: number, patch: Partial<InputShapeRow>) => {
    setRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  return (
    <NodeViewWrapper className="input-shape-node react-renderer mt-4">
      <div
        className="p-2 border rounded-md bg-sky-50 select-none border-sky-300 flex flex-col gap-2"
        contentEditable={false}
        suppressContentEditableWarning
      >
        <Text.Label className="text-sky-600 font-medium">
          This agent takes these inputs...
        </Text.Label>
        <div className="flex flex-col gap-2 w-full bg-background rounded-md p-2 border border-sky-300 prose-ignore">
          {rows.length === 0 ? (
            <Text.Caption className="text-muted-text">
              No inputs — the agent takes a single text prompt.
            </Text.Caption>
          ) : null}
          {rows.map((row, index) => {
            const error = nameError(row, index, rows);
            return (
              <div key={index} className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    className="w-40 font-mono"
                    value={row.name}
                    placeholder="name"
                    aria-invalid={error !== null}
                    disabled={!editable}
                    onChange={(e) => updateRow(index, { name: e.target.value })}
                  />
                  <Select
                    value={row.type}
                    disabled={!editable}
                    onValueChange={(v) => {
                      if (isFieldType(v)) {
                        updateRow(index, { type: v });
                      }
                    }}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DG.INPUT_SHAPE_FIELD_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {TYPE_LABELS[t]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    className="flex-1 min-w-40"
                    value={row.description ?? ''}
                    placeholder="Description (optional)"
                    disabled={!editable}
                    onChange={(e) =>
                      updateRow(index, {
                        description:
                          e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                  {editable ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Remove input"
                      onClick={() =>
                        setRows(rows.filter((_, i) => i !== index))
                      }
                    >
                      <Icon icon="trash" className="text-destructive" />
                    </Button>
                  ) : null}
                </div>
                {error !== null ? (
                  <Text.Caption className="text-destructive">
                    {error}
                  </Text.Caption>
                ) : null}
              </div>
            );
          })}
          {editable ? (
            <Button
              className="w-fit"
              variant="outline"
              onClick={() =>
                setRows([
                  ...rows,
                  { name: '', type: 'string', description: null },
                ])
              }
            >
              <Icon icon="add" />
              Add Input
            </Button>
          ) : null}
        </div>
      </div>
    </NodeViewWrapper>
  );
};
